import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  TrendingUp, TrendingDown, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock,
  BarChart3, Activity, Target, Brain, LineChart
} from 'lucide-react';

// FAQ Data for Schema
const faqData = [
  {
    question: "MACD ne anlama gelir?",
    answer: "MACD, Moving Average Convergence Divergence (Hareketli Ortalama Yakınsama Iraksama) anlamına gelir. İki hareketli ortalama arasındaki ilişkiyi gösteren bir momentum göstergesidir."
  },
  {
    question: "MACD çizgisi ve sinyal çizgisi nedir?",
    answer: "MACD çizgisi, 12 ve 26 periyotluk EMA'ların farkıdır. Sinyal çizgisi ise MACD çizgisinin 9 periyotluk EMA'sıdır. Bu iki çizginin kesişimleri alım-satım sinyali olarak yorumlanır."
  },
  {
    question: "MACD histogram ne gösterir?",
    answer: "MACD histogram, MACD çizgisi ile sinyal çizgisi arasındaki farkı gösterir. Histogram büyüyorsa momentum artıyor, küçülüyorsa momentum azalıyor demektir."
  },
  {
    question: "MACD ile nasıl trade yapılır?",
    answer: "MACD çizgisi sinyal çizgisini yukarı keserse alım, aşağı keserse satım sinyali olabilir. Ayrıca sıfır çizgisinin üstü yükseliş, altı düşüş trendi olarak yorumlanır. Divergence'lar da önemli sinyallerdir."
  },
  {
    question: "MACD ve RSI birlikte nasıl kullanılır?",
    answer: "MACD trend yönünü ve momentumu gösterirken, RSI aşırı alım/satım bölgelerini gösterir. İkisi birlikte kullanıldığında daha güvenilir sinyaller elde edilir. Örneğin, MACD yukarı kesişim + RSI oversold = güçlü alım sinyali."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "MACD Nedir? Moving Average Convergence Divergence Kullanımı",
    "description": "MACD göstergesi nedir, nasıl hesaplanır ve nasıl yorumlanır? Kripto trading'de MACD kullanımı rehberi.",
    "author": { "@type": "Organization", "name": "Borsamex" },
    "publisher": {
      "@type": "Organization",
      "name": "Borsamex",
      "logo": { "@type": "ImageObject", "url": "https://borsamex.com/logo512.png" }
    },
    "datePublished": "2026-04-02",
    "dateModified": "2026-04-02",
    "mainEntityOfPage": "https://borsamex.com/rehber/macd-nedir"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const MACDNedirPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="MACD Nedir? Moving Average Convergence Divergence Kullanımı | Borsamex"
        description="MACD göstergesi nedir, nasıl hesaplanır ve nasıl yorumlanır? Kripto trading'de MACD çizgisi, sinyal çizgisi ve histogram kullanımı rehberi."
        keywords="macd nedir, macd göstergesi, macd nasıl yorumlanır, macd sinyal çizgisi, macd histogram, macd divergence, kripto teknik analiz"
        canonical="https://borsamex.com/rehber/macd-nedir"
        structuredData={structuredData}
      />

      {/* Breadcrumb */}
      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white transition-colors">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">MACD Nedir?</span>
          </div>
        </div>
      </div>

      {/* Article */}
      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          {/* Header */}
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">Teknik Analiz</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>7 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              MACD Nedir? (Moving Average Convergence Divergence)
            </h1>
            
            {/* Featured Snippet Target */}
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                <strong className="text-white">MACD (Moving Average Convergence Divergence)</strong>, 
                iki hareketli ortalama arasındaki ilişkiyi gösteren trend takip ve momentum göstergesidir. 
                <strong className="text-brand-green"> MACD çizgisi</strong>, 
                <strong className="text-blue-400"> sinyal çizgisi</strong> ve 
                <strong className="text-purple-400"> histogram</strong> olmak üzere 3 bileşenden oluşur.
              </p>
            </div>
          </motion.header>

          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }}
            className="prose prose-invert prose-slate max-w-none"
          >
            
            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <LineChart className="h-4 w-4 text-purple-400" />
                </div>
                MACD Göstergesi Ne İşe Yarar?
              </h2>
              
              <p className="text-slate-300 mb-4">
                MACD, 1970'lerde Gerald Appel tarafından geliştirilmiş ve teknik analizin en popüler 
                göstergelerinden biri haline gelmiştir. Hem <strong className="text-white">trend yönünü</strong> hem de 
                <strong className="text-white"> momentum gücünü</strong> gösterir.
              </p>
              
              <p className="text-slate-300 mb-6">
                MACD'nin temel amacı, trendlerin ne zaman başladığını, ne zaman bittiğini ve momentum değişimlerini 
                tespit etmektir. Bu bilgi, potansiyel giriş ve çıkış noktalarını belirlemek için kullanılır.
              </p>
              
              <Card className="border-slate-700 bg-slate-900/50 mb-6">
                <CardContent className="p-6">
                  <h3 className="text-white font-bold mb-4">MACD'nin 3 Bileşeni</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-brand-green"></div>
                      <div>
                        <span className="text-white font-medium">MACD Çizgisi:</span>
                        <span className="text-slate-400 ml-2">12 EMA - 26 EMA</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-red-400"></div>
                      <div>
                        <span className="text-white font-medium">Sinyal Çizgisi:</span>
                        <span className="text-slate-400 ml-2">MACD'nin 9 periyotluk EMA'sı</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-purple-400"></div>
                      <div>
                        <span className="text-white font-medium">Histogram:</span>
                        <span className="text-slate-400 ml-2">MACD - Sinyal Çizgisi farkı</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Section 2 - How to Read */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 text-blue-400" />
                </div>
                MACD Nasıl Yorumlanır?
              </h2>
              
              <div className="space-y-6 mb-6">
                <div>
                  <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-400" />
                    Yükseliş Sinyalleri (Bullish)
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                      <span>MACD çizgisi sinyal çizgisini <strong className="text-green-400">yukarı</strong> kesiyor</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                      <span>MACD sıfır çizgisinin <strong className="text-green-400">üstüne</strong> geçiyor</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                      <span>Histogram negatiften pozitife dönüyor</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-red-400" />
                    Düşüş Sinyalleri (Bearish)
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                      <span>MACD çizgisi sinyal çizgisini <strong className="text-red-400">aşağı</strong> kesiyor</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                      <span>MACD sıfır çizgisinin <strong className="text-red-400">altına</strong> geçiyor</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                      <span>Histogram pozitiften negatife dönüyor</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Inline Signal Box */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Örneği</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">BTC-USDT</span>
                      <Badge className="bg-green-500/20 text-green-400">LONG</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      MACD bullish crossover + RSI 35'ten yükseliş + Destek testi
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>Çoklu gösterge konfirmasyonu</span>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Borsamex, MACD crossover'larını RSI ve diğer göstergelerle birlikte değerlendirerek sinyal üretir.
                  </p>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Canlı Sinyalleri İncele <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            {/* Section 3 - Divergence */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-green-400" />
                </div>
                MACD Divergence (Uyumsuzluk)
              </h2>
              
              <p className="text-slate-300 mb-6">
                Divergence, fiyat ile MACD'nin farklı yönlerde hareket etmesidir. Bu durum, trend 
                dönüşünün yaklaştığına işaret edebilir.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <Card className="border-green-500/30 bg-green-950/20">
                  <CardContent className="p-4">
                    <h3 className="font-bold text-green-400 mb-2">Bullish Divergence</h3>
                    <p className="text-slate-300 text-sm">
                      Fiyat yeni <strong>dip</strong> yaparken, MACD daha <strong>yüksek dip</strong> yapıyor. 
                      Potansiyel yükseliş sinyali.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-red-500/30 bg-red-950/20">
                  <CardContent className="p-4">
                    <h3 className="font-bold text-red-400 mb-2">Bearish Divergence</h3>
                    <p className="text-slate-300 text-sm">
                      Fiyat yeni <strong>zirve</strong> yaparken, MACD daha <strong>düşük zirve</strong> yapıyor. 
                      Potansiyel düşüş sinyali.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* Section 4 - AI Trading */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Brain className="h-4 w-4 text-brand-green" />
                </div>
                AI Trading'de MACD Kullanımı
              </h2>
              
              <p className="text-slate-300 mb-4">
                Profesyonel trading sistemleri MACD'yi tek başına değil, diğer göstergelerle birlikte 
                değerlendirir. Borsamex'in AI sistemi şu kombinasyonları analiz eder:
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">MACD + <Link to="/rehber/rsi-nedir" className="text-brand-green hover:underline">RSI</Link>:</strong> Momentum ve aşırı alım/satım birlikte değerlendirilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">MACD + Hacim:</strong> Yüksek hacimli crossover'lar daha güvenilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">MACD + <Link to="/rehber/destek-direnc-nedir" className="text-brand-green hover:underline">Destek/Direnç</Link>:</strong> Kritik seviyelerdeki sinyaller öncelikli</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Çoklu zaman dilimi:</strong> 15dk, 1s, 4s MACD uyumu kontrol edilir</span>
                </li>
              </ul>
              
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-4">
                  <p className="text-amber-200 text-sm flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                    MACD gecikmeli bir göstergedir. Hızlı piyasalarda sinyaller gecikebilir. Her zaman stop-loss kullanın.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sık Sorulan Sorular</h2>
              
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Related Articles */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">İlgili Makaleler</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/rehber/rsi-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">RSI Nedir?</h3>
                      <p className="text-slate-400 text-sm">MACD ile birlikte kullanılan momentum göstergesi</p>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/fibonacci-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">Fibonacci Nedir?</h3>
                      <p className="text-slate-400 text-sm">Hedef ve geri çekilme seviyelerini belirleme</p>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/destek-direnc-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">Destek ve Direnç Nedir?</h3>
                      <p className="text-slate-400 text-sm">MACD sinyallerini doğrulamak için kritik seviyeler</p>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/kripto-sinyali-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">Kripto Sinyali Nedir?</h3>
                      <p className="text-slate-400 text-sm">Sinyallerin temel yapısı ve bileşenleri</p>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            <ShareBox 
              title="MACD Nedir? Kripto Teknik Analiz Rehberi"
              url="/rehber/macd-nedir"
              hashtags="#kripto #MACD #teknikanaliz #trading"
            />

          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber/rsi-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Önceki: RSI Nedir?
            </Link>
            <Link to="/rehber/fibonacci-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              Sonraki: Fibonacci Nedir?
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-purple-900/20 to-brand-green/10 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              MACD + RSI + 200 Gösterge Birlikte
            </h3>
            <p className="text-slate-400 mb-6">
              Borsamex'in AI sistemi MACD dahil yüzlerce göstergeyi analiz ederek sinyal üretir.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Dene <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default MACDNedirPage;
